import React, { useState, useEffect, useRef } from 'react';
import { PanelRightClose, PanelRightOpen, GripVertical, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ResizableSidebarProps {
    isOpen: boolean;
    onToggle: () => void;
    children: React.ReactNode;
    initialWidth?: number;
    width?: number; // Added controlled width
    minWidth?: number;
    maxWidth?: number;
    side?: 'left' | 'right';
    title?: string;
    onInfoClick?: () => void;
    showInfo?: boolean;
}

export default function ResizableSidebar({
    isOpen,
    onToggle,
    children,
    initialWidth = 300,
    width: controlledWidth,
    minWidth = 300,
    maxWidth = 800,
    side = 'right',
    title,
    onInfoClick,
    showInfo = false,
}: ResizableSidebarProps) {
    const [internalWidth, setInternalWidth] = useState(initialWidth);
    const width = controlledWidth || internalWidth; // Use controlled if available

    const [isResizing, setIsResizing] = useState(false);
    const sidebarRef = useRef<HTMLDivElement>(null);

    // Mouse Move Handler for Resizing
    const handleMouseMove = (e: MouseEvent) => {
        if (!isResizing) return;

        let newWidth;
        if (side === 'right') {
            // Calculate width from right edge of screen/container
            // Assuming sidebar is anchored right
            // We need to know where the Sidebar starts?
            // Easier: newWidth = window.innerWidth - e.clientX
            // But this assumes full screen.
            // Better: deltaX

            // Simplified for 'right' sidebar:
            // DeltaX < 0 means increasing width (move left)
            // DeltaX > 0 means decreasing width (move right)
            // Wait, movementX is reliable only in pointer lock

            // Standard resize logic:
            const containerRect = sidebarRef.current?.parentElement?.getBoundingClientRect();
            if (!containerRect) return;

            const rightEdge = containerRect.right;
            newWidth = rightEdge - e.clientX;

        } else {
            newWidth = e.clientX; // Left sidebar
        }

        if (newWidth < minWidth) newWidth = minWidth;
        if (newWidth > maxWidth) newWidth = maxWidth;
        setInternalWidth(newWidth);
    };

    const handleMouseUp = () => {
        setIsResizing(false);
        document.body.style.cursor = 'default';
    };

    // Attach/Detach listeners
    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'col-resize';
            // Disable text selection during resize
            document.body.style.userSelect = 'none';
        } else {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = 'auto';
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing]);


    if (!isOpen) {
        return (
            <div className="h-full border-l border-gray-200 bg-white flex flex-col items-center py-4 w-12 flex-shrink-0 transition-all">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onToggle}
                    className="text-gray-400 hover:text-blue-600"
                    title="Open Properties"
                >
                    <PanelRightOpen className="h-4 w-4" />
                </Button>
                {title && (
                    <div className="mt-8">
                        <div className="rotate-90 whitespace-nowrap text-xs font-bold text-gray-700 tracking-wide transform translate-y-8">
                            {title.toUpperCase()}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div
            ref={sidebarRef}
            className="h-full bg-white border-l border-gray-200 flex flex-col shadow-xl relative transition-width duration-0"
            style={{ width: `${width}px` }}
        >
            {/* Drag Handle */}
            <div
                className={`absolute top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 transition-colors z-50
                    ${side === 'right' ? 'left-0' : 'right-0'}
                    ${isResizing ? 'bg-blue-500' : 'bg-transparent'}
                `}
                onMouseDown={() => setIsResizing(true)}
            >
                {/* Visual Grip Indicator centered vertically */}
                <div className="absolute top-1/2 -translate-y-1/2 -left-1.5 flex items-center justify-center pointer-events-none opacity-0 hover:opacity-100 transition-opacity">
                    <GripVertical className="h-6 w-3 text-gray-400" />
                </div>
            </div>

            {/* Header */}
            <div className="h-12 border-b border-gray-100 flex items-center justify-between px-4 bg-gray-50/50 flex-shrink-0">
                <span className="font-semibold text-sm text-gray-700 font-sans tracking-tight">
                    {title || 'Properties'}
                </span>
                <div className="flex items-center gap-0.5">
                    {/* Info toggle */}
                    {onInfoClick && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onInfoClick}
                            className={`h-7 w-7 transition-colors ${
                                showInfo
                                    ? 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100'
                                    : 'text-gray-400 hover:text-gray-900'
                            }`}
                            title={showInfo ? 'Show Config' : 'Show Node Info'}
                        >
                            <Info className="h-4 w-4" />
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onToggle}
                        className="h-7 w-7 text-gray-400 hover:text-gray-900"
                    >
                        <PanelRightClose className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* Scrollable Content — scroll position is preserved across re-renders */}
            <div
                ref={(el) => {
                    // Restore saved scroll position immediately after any re-render
                    if (el && el.dataset.scrollTop) {
                        el.scrollTop = parseInt(el.dataset.scrollTop, 10);
                    }
                }}
                className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar bg-white"
                onScroll={(e) => {
                    // Save scroll position into the DOM element so the ref callback can restore it
                    const el = e.currentTarget;
                    el.dataset.scrollTop = String(el.scrollTop);
                }}
            >
                {children}
            </div>
        </div>
    );
}
